import {
  trackReelTranscriptRowAutoExpanded,
  trackReelTranscriptRowCollapsed,
  trackReelTranscriptRowExpanded,
} from '@src/analytics/EventTracker';

import type { ReelTranscriptSurface } from '@src/analytics/EventTracker';
import type { ReelTranscriptCollapseView } from '@src/components/transcript/reelStructure/useReelTranscriptCollapse';
import type { ReelTranscriptStructure } from '@src/services/reel/reelTranscriptStructure';

/**
 * PLAT-11164 — the collapse view, instrumented.
 *
 * The base hook and the heading components stay analytics-free: this wrapper
 * is where a toggle becomes a collapsed-or-expanded event (the CURRENT state
 * says which way it went) and a reveal becomes one auto-expanded event per
 * level that actually opens. Host-side by design — the host knows the
 * surface, and PLAT-11172's Creator Studio host wraps with `creator_studio`.
 */
export function instrumentReelTranscriptCollapse(
  collapse: ReelTranscriptCollapseView,
  structure: ReelTranscriptStructure,
  surface: ReelTranscriptSurface,
): ReelTranscriptCollapseView {
  const sectionsInChapter = (chapterId: string) =>
    structure.chapters.find(chapter => chapter.chapterId === chapterId)
      ?.sections.length ?? 0;

  return {
    ...collapse,
    toggleChapter: (chapterId: string) => {
      if (collapse.collapsedChapterIds.has(chapterId)) {
        trackReelTranscriptRowExpanded({
          surface,
          level: 'chapter',
          row_id: chapterId,
        });
      } else {
        trackReelTranscriptRowCollapsed({
          surface,
          level: 'chapter',
          row_id: chapterId,
          sections_hidden: sectionsInChapter(chapterId),
        });
      }
      collapse.toggleChapter(chapterId);
    },
    toggleSection: (sectionId: string) => {
      if (collapse.collapsedSectionIds.has(sectionId)) {
        trackReelTranscriptRowExpanded({
          surface,
          level: 'section',
          row_id: sectionId,
        });
      } else {
        trackReelTranscriptRowCollapsed({
          surface,
          level: 'section',
          row_id: sectionId,
          sections_hidden: 1,
        });
      }
      collapse.toggleSection(sectionId);
    },
    revealSection: (sectionId, chapterId, reason) => {
      // One event per level that actually opens: the reveal clears every
      // ancestor fold, but only the ones that WERE folded count as openings.
      const effectiveReason = reason ?? 'jump';
      if (
        chapterId !== undefined &&
        collapse.collapsedChapterIds.has(chapterId)
      ) {
        trackReelTranscriptRowAutoExpanded({
          surface,
          level: 'chapter',
          row_id: chapterId,
          reason: effectiveReason,
        });
      }
      if (collapse.collapsedSectionIds.has(sectionId)) {
        trackReelTranscriptRowAutoExpanded({
          surface,
          level: 'section',
          row_id: sectionId,
          reason: effectiveReason,
        });
      }
      collapse.revealSection(sectionId, chapterId, reason);
    },
  };
}
